import { createContext, useContext, useMemo, type ReactNode } from "react";
import { carespaceRecordUrl } from "./careNavigationUrls";
import { stringifyCsv } from "./dashboardsExport";

/** What a drill-down CSV carries per CareSpace: the id, its navigator and its clinic. */
export type CarespaceRecord = { id: string; navigatorName: string; clinicName: string };

type CarespaceRecords = {
  /** Download file names start with this, e.g. "care-navigation". */
  filePrefix: string;
  records: ReadonlyMap<string, CarespaceRecord>;
};

const CarespaceRecordsContext = createContext<CarespaceRecords>({
  filePrefix: "carespaces",
  records: new Map(),
});

export function CarespaceRecordsProvider(props: {
  filePrefix: string;
  records: CarespaceRecord[];
  children: ReactNode;
}) {
  const value = useMemo(
    () => ({
      filePrefix: props.filePrefix,
      records: new Map(props.records.map((record) => [record.id, record])),
    }),
    [props.filePrefix, props.records],
  );
  return <CarespaceRecordsContext value={value}>{props.children}</CarespaceRecordsContext>;
}

export function useCarespaceRecords() {
  return useContext(CarespaceRecordsContext);
}

export function carespaceRecordsCsv(records: CarespaceRecord[]) {
  const safeCell = (value: string) => (/^[=+\-@\t\r]/.test(value) ? `'${value}` : value);
  return stringifyCsv(
    [...new Map(records.map((record) => [record.id, record])).values()].map((record) => ({
      "Carespace ID": record.id,
      "Care Navigator": safeCell(record.navigatorName),
      "Clinic name": safeCell(record.clinicName),
      "Carespace URL": carespaceRecordUrl(record.id) ?? "",
    })),
  );
}

export function downloadCarespaceRecords(
  records: CarespaceRecord[],
  label: string,
  filePrefix: string,
) {
  const url = URL.createObjectURL(
    new Blob(["﻿", carespaceRecordsCsv(records)], { type: "text/csv;charset=utf-8" }),
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = `${filePrefix}-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}
